import type { Mutation } from '@bp/bp-graphql-types';
import type { DataFields, UpdateResolver } from '@urql/exchange-graphcache';
import { groupedUpdateList } from './groupedUpdateList';

export type mutations = (keyof Mutation)[];
export type FieldName = string;

const createUpdateResolver =
  (fieldName: FieldName, mutation: keyof Mutation): UpdateResolver =>
  (result, args, cache, info) => {
    const isDelete = /delete.*/.test(mutation);

    const typeName = mutation.match(
      /(create|delete|disconnect|merge|bulkUpdateOrCreate|addMembersTo|removeMembersFrom|updateMembersRoleIn)(Bp)?(.+?)s?$/,
    )?.[3];

    if (!typeName) throw new Error('typeName cannot be calculated');
    const inspect = cache.inspectFields('Query');
    const filteredInspects = inspect.filter((i) => i.fieldName === fieldName);
    const cacheResults = filteredInspects.map((i) => ({
      ...i,
      cache: cache.resolve('Query', i.fieldKey) as string[],
    }));

    if (isDelete) {
      const uuids: string[] = getUuidFromArgs(args, typeName);

      cacheResults.forEach((r) => {
        const linkArray = r.cache.filter((c) => !uuids.includes(c));
        cache.link('Query', r.fieldKey, linkArray);
      });
      return result;
    }

    const newKeys = getUuidsFromResponse(result, info.fieldName, typeName);
    cacheResults.forEach((r) => {
      const linkArray = [...r.cache, ...newKeys];
      cache.link('Query', r.fieldKey, linkArray);
    });
    return result;
  };

export const updateFunctions = Object.fromEntries(
  Object.entries(groupedUpdateList).flatMap(([fieldName, mutations]) =>
    mutations.map((mutation) => [mutation, createUpdateResolver(fieldName, mutation)]),
  ),
);

const getUuidsFromResponse = (result: DataFields, fieldName: FieldName, typeName: string): string[] => {
  const response = result?.[fieldName];
  const uuids: string[] = [];

  // Helper function to recursively search for objects with matching __typename
  const findUuids = (obj: unknown) => {
    if (!obj || typeof obj !== 'object') return;

    // Type guard to check if object has the required properties
    const hasTypeAndUuid = (value: unknown): value is { __typename?: string; uuid: string } => {
      return (
        typeof value === 'object' &&
        value !== null &&
        'uuid' in value &&
        (('__typename' in value && typeof value.__typename === 'string') || !('__typename' in value))
      );
    };

    // If object has matching __typename and uuid, collect it
    if (hasTypeAndUuid(obj) && obj.__typename === typeName && obj.uuid) {
      uuids.push(`${typeName}:${obj.uuid}`);
    }

    // Recursively search through arrays and object properties
    if (Array.isArray(obj)) {
      obj.forEach(findUuids);
    } else {
      Object.values(obj).forEach(findUuids);
    }
  };

  if (!response) return [];

  findUuids(response);
  return uuids;
};

type ArgsWithUuid = {
  uuid?: string;
  id?: string;
  where?: {
    uuid?: string;
    id?: string;
  };
};

const getUuidFromArgs = (args: unknown, typeName: string): string[] => {
  // Type guard to check if args matches our expected structure
  const hasUuidOrId = (value: unknown): value is ArgsWithUuid => {
    if (!value || typeof value !== 'object') return false;

    const obj = value as Record<string, unknown>;
    return (
      ('uuid' in obj && typeof obj.uuid === 'string') ||
      ('id' in obj && typeof obj.id === 'string') ||
      ('where' in obj &&
        typeof obj.where === 'object' &&
        obj.where !== null &&
        (('uuid' in obj.where && typeof obj.where.uuid === 'string') ||
          ('id' in obj.where && typeof obj.where.id === 'string')))
    );
  };

  if (!hasUuidOrId(args)) {
    throw new Error('Invalid args structure: missing uuid or id');
  }

  const uuid = args.where?.uuid ?? args.uuid ?? args.where?.id ?? args.id;
  return [`${typeName}:${uuid}`];
};
